import React, { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import FastMarquee from 'react-fast-marquee';
import { createPortal } from 'react-dom';

import { useClassName, useResize, useWindowSize } from 'common/hooks';
import { ControlledTabs } from 'public/components/Tabs';
import { SVGIcon } from 'common/components';
import { Modal } from 'common/lazy';
import { STANDARD_EVENTS } from 'utils/analytics/trackV2';

import useSlider from '../../../Home/Components/useSlider';
import CarouselArrows from '../CarouselArrows';
import CarouselControls from '../CarouselControls';
import CarouselCounter from '../CarouselCounter';
import CarouselTypeE from './CarouselTypeE';
import CarouselTypeF from './CarouselTypeF';
import CarouselAsset from './CarouselAsset';
import CarouselCard from './CarouselCard';

import useCustomStyle from '../../hooks';
import useLoop from '../../../Home/Components/useLoop';

import './carousel.less';
import 'swiper/swiper.less';

function Tabs({ setTagIndex, tagIndex, tags, handleTrack, tabsVariant }) {
  const classNames = useClassName('CLPCarousel');

  if (!tags?.length) return null;

  return (
    <ControlledTabs
      names={tags}
      selectedIndex={tagIndex}
      styles={{
        container: classNames(['tabs', tabsVariant && `tabs--${tabsVariant}`]),
      }}
      onClick={(tagName) => {
        setTagIndex(tags.findIndex((tag) => tag === tagName));
      }}
      handleTrack={handleTrack}
    />
  );
}

function getBreakpoints(component) {
  const slidesPerView = component?.fields?.settings?.find(
    (setting) => setting.fields.key === '--slides-per-view'
  );
  if (!slidesPerView)
    return {
      0: {
        slidesPerView: 'auto',
      },
    };

  return {
    0: {
      slidesPerView: Number(slidesPerView.fields.mobileValue),
    },
    767: {
      slidesPerView: Number(slidesPerView.fields.tabletValue),
    },
    1128: {
      slidesPerView: Number(slidesPerView.fields.desktopValue),
    },
  };
}

function getResponsiveAsset(slide, viewport) {
  if (slide?.sys?.contentType?.sys?.id !== 'lpAsset') return null;

  if (viewport.isMobile && slide.fields.mobile) return slide.fields.mobile;
  if (viewport.isTablet && slide.fields.tablet) return slide.fields.tablet;
  if (viewport.isDesktop && slide.fields.desktop) return slide.fields.desktop;

  return (
    slide.fields.desktop || slide.fields.tablet || slide.fields.mobile || null
  );
}

function getSlideAspectRatio(slide, viewport, fallback) {
  const asset = getResponsiveAsset(slide, viewport);
  const dimensions = asset?.fields?.file?.details?.image;

  if (dimensions?.width && dimensions?.height) {
    return dimensions.width / dimensions.height;
  }

  const [fallbackWidth, fallbackHeight] = String(fallback)
    .split('/')
    .map(Number);

  return fallbackWidth && fallbackHeight ? fallbackWidth / fallbackHeight : 1;
}

function getVariableSlideWidth(height, aspectRatio) {
  const value = String(height).trim();
  const match = value.match(/^(-?(?:\d+\.?\d*|\.\d+))([a-z%]+)$/i);

  if (match) {
    return `${Number(match[1]) * aspectRatio}${match[2]}`;
  }

  return 'auto';
}

function toReactStyleName(property) {
  return property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

function Slider({ component, tagIndex, paused, setPaused, handleTrack }) {
  const classNames = useClassName('CLPCarousel');
  const [, styles] = useCustomStyle(component.fields.settings);
  const viewport = useWindowSize();
  const [props, methods, state] = useSlider();
  const breakpoints = getBreakpoints(component);
  const tags = [
    ...new Set(component.fields.slides.map((a) => a.fields.tags).flat()),
  ].filter(Boolean);
  const slides = component.fields.groupTags
    ? component.fields.slides.filter((slide) =>
        slide.fields?.tags?.includes(tags[tagIndex])
      )
    : component.fields.slides;
  const defaultLoop = useLoop({
    breakpoints,
    slidesLength: slides.length,
    showPreview: styles['--show-preview'] !== 'false',
  });
  const variableWidth = styles['--carousel-layout'] === 'variable-width';
  const edgeGradients =
    variableWidth && styles['--carousel-edge-gradients'] !== 'false';
  const slideHeight =
    styles['--slide-height'] || (viewport.isMobile ? '190px' : '285px');
  const variableWidthLoop =
    styles['--carousel-variable-width-loop'] === 'true' && slides.length > 1;

  // Opt-in: on desktop the wrapper leaves room for the loop's clones to show
  // in the left margin, so drop the infinite loop there. Keeps sliding and
  // bullets, unlike the regular no-loop mode. Mobile/tablet keep the loop.
  const hasDesktopLoopSetting = styles['--carousel-desktop-loop'] === 'false';
  const desktopNoLoop =
    hasDesktopLoopSetting && viewport.isDesktop && slides.length > 1;
  const [atEnd, setAtEnd] = useState(false);
  const [endBullet, setEndBullet] = useState(null);
  const isEndRef = useRef(false);

  const fixedSlideWidth = styles['--slide-width'];
  const sliderBehavior = variableWidth
    ? {
        loop: variableWidthLoop,
        loopAdditionalSlides: variableWidthLoop ? slides.length : 0,
        slidesPerView: 'auto',
      }
    : fixedSlideWidth
    ? {
        loop: slides.length > 1 && !desktopNoLoop,
        loopAdditionalSlides: desktopNoLoop ? 0 : slides.length,
        slidesPerView: 'auto',
      }
    : defaultLoop;
  const transitionSpeed = parseInt(styles['--transition-speed']) || 300;
  const hiddenCounter =
    styles['--carousel-hidden-counter'] === 'true' ? true : false;
  const hiddenArrows =
    styles['--carousel-hidden-arrows'] === 'true' ? true : false;
  const hiddenOverflow = Object.keys(breakpoints).length > 1;
  const assetLinksEnabled = styles['--asset-links-enabled'] === 'true';
  const disableDragWithoutLoop =
    styles['--disable-drag-without-loop'] === 'true';
  const singleSlideFullWidth = styles['--single-slide-full-width'] !== 'false';
  const singleSlideAlignment = styles['--single-slide-alignment'];
  // Without the loop the last slides can't reach the left edge, so several
  // bullets share the end position: the clicked one lights up, else the last.
  const currentBullet =
    desktopNoLoop && atEnd ? endBullet ?? slides.length - 1 : state.activeIndex;

  return (
    <>
      <Swiper
        key={
          disableDragWithoutLoop
            ? `drag-${sliderBehavior.pending ? 'pending' : sliderBehavior.loop}`
            : hasDesktopLoopSetting
            ? `desktop-no-loop-${desktopNoLoop}`
            : undefined
        }
        height={parseInt(styles['--height'] || slideHeight) || 500}
        centeredSlides={styles['--centered-slides'] !== 'false'}
        slidesPerView="auto"
        spaceBetween={parseInt(styles['--space-between']) || 0}
        speed={transitionSpeed}
        {...props}
        {...sliderBehavior}
        {...(hasDesktopLoopSetting && { initialSlide: state.activeIndex })}
        {...(desktopNoLoop && {
          onProgress: ({ isBeginning, isEnd }) => {
            isEndRef.current = isEnd;
            setAtEnd(isEnd && !isBeginning);
            if (!isEnd) setEndBullet(null);
          },
        })}
        allowTouchMove={
          disableDragWithoutLoop ? Boolean(sliderBehavior.loop) : undefined
        }
        className={classNames([
          'wrapper',
          !sliderBehavior.loop && !variableWidth && !desktopNoLoop && 'no-loop',
          (hiddenOverflow || variableWidth) && 'hidden-overflow',
          variableWidth && 'variable-width',
          variableWidth && !sliderBehavior.loop && 'variable-width-no-loop',
          edgeGradients && 'edge-gradients',
          slides.length === 1 &&
            singleSlideAlignment === 'left' &&
            'single-left',
          styles['--equal-height'] === 'true' && 'equal-height',
        ])}
        style={{
          ...Object.fromEntries(
            Object.entries(styles)
              .filter(([key]) => key.startsWith('--swiper'))
              .map(([key, value]) => [key.replace('--swiper-', ''), value])
          ),
          ...(variableWidth && { height: slideHeight }),
        }}>
        {slides.map((slide, index) => {
          const slideStyles = Object.fromEntries(
            Object.entries(styles)
              .filter(([key]) => key.startsWith('--slide-'))
              .map(([key, value]) => [
                toReactStyleName(key.replace('--slide-', '')),
                value,
              ])
          );
          const aspectRatio = getSlideAspectRatio(
            slide,
            viewport,
            styles['--carousel-fallback-aspect-ratio'] || '1 / 1'
          );

          return (
            <SwiperSlide
              key={index}
              className={classNames([
                'slide-container',
                slides.length === 1 && singleSlideFullWidth && 'single',
                variableWidth && 'variable-slide',
              ])}
              style={{
                ...slideStyles,
                ...(variableWidth && {
                  width: getVariableSlideWidth(slideHeight, aspectRatio),
                  height: slideHeight,
                }),
              }}>
              {({ isActive }) => {
                return slide.sys.contentType.sys.id === 'lpAsset' ? (
                  <CarouselAsset
                    linkEnabled={assetLinksEnabled}
                    slide={slide}
                    setPaused={setPaused}
                    settings={component.fields.settings}
                    isActive={isActive}
                  />
                ) : (
                  <CarouselCard
                    slide={slide}
                    setPaused={setPaused}
                    isActive={isActive}
                    handleTrack={handleTrack}
                  />
                );
              }}
            </SwiperSlide>
          );
        })}
      </Swiper>
      {slides.length > 1 && (sliderBehavior.loop || desktopNoLoop) && (
        <CarouselControls settings={component.fields.settings}>
          {!hiddenCounter && (
            <CarouselCounter
              current={currentBullet + 1}
              total={slides.length}
              next={methods.slideNext}
              goTo={(index) => {
                methods.slideTo(index);
                setEndBullet(isEndRef.current ? index : null);
              }}
              paused={paused}
              autoplayDuration={component.fields.autoplayDuration}
              type={component.fields.type}
              theme={component.fields.theme}
              style={Object.fromEntries(
                Object.entries(styles).filter(([key]) =>
                  key.startsWith('--bullet-')
                )
              )}
            />
          )}
          {!hiddenArrows && (
            <CarouselArrows
              next={methods.slideNext}
              prev={methods.slidePrev}
              type={component.fields.type}
              theme={component.fields.theme}
              settings={component.fields.settings}
              style={Object.fromEntries(
                Object.entries(styles)
                  .filter(([key]) => key.startsWith('--carousel-nav-'))
                  .map(([key, value]) => [
                    toReactStyleName(key.replace('--carousel-nav-', '')),
                    value,
                  ])
              )}
              trackDirection={(direction) =>
                handleTrack &&
                handleTrack({
                  event: STANDARD_EVENTS.carouselSlide,
                  params: {
                    carousel_name: component?.fields?.name,
                    direction,
                  },
                })
              }
            />
          )}
        </CarouselControls>
      )}
    </>
  );
}

// Manual fill: duplicate slides until we have enough to cover wide viewports
// without depending on react-fast-marquee's autoFill (which causes a
// ResizeObserver loop in dense layouts).
const MARQUEE_MIN_SLIDES = 20;

function Marquee({ component, onClick, ...props }) {
  const [, styles] = useCustomStyle(component.fields.settings);
  const slides = component.fields.slides;
  const repeatCount = Math.max(
    1,
    Math.ceil(MARQUEE_MIN_SLIDES / Math.max(slides.length, 1))
  );
  const filledSlides = Array(repeatCount).fill(slides).flat();

  return (
    <FastMarquee pauseOnHover {...props}>
      {filledSlides.map((slide, idx) => {
        return (
          <div
            key={`${slide.sys.id}-${idx}`}
            style={{
              margin: '8px',
              borderRadius: '32px',
              overflow: 'hidden',
              cursor: 'pointer',
              ...Object.fromEntries(
                Object.entries(styles)
                  .filter(([key]) => key.startsWith('--slide'))
                  .map(([key, value]) => [key.replace('--slide-', ''), value])
              ),
            }}
            onClick={() => onClick(slide.sys.id)}>
            {slide.sys.contentType.sys.id === 'lpAsset' ? (
              <CarouselAsset
                slide={slide}
                settings={component.fields.settings}
              />
            ) : (
              <CarouselCard slide={slide} />
            )}
          </div>
        );
      })}
    </FastMarquee>
  );
}

function TypeDCarousel({ component }) {
  const classNames = useClassName('CLPCarousel');
  const [setRef] = useCustomStyle(component.fields.settings);
  const { width: viewportWidth } = useWindowSize();
  const [containerNode, setContainerNode] = useState(null);
  const { width: containerWidth } = useResize(containerNode);
  const isReady = Boolean(viewportWidth && containerWidth);

  return (
    <div
      ref={(node) => {
        setContainerNode(node);
        setRef(node);
      }}
      className={classNames('type-d')}
      style={{ position: 'relative' }}>
      {isReady && (
        <Marquee
          key={`${viewportWidth}-${containerWidth}`}
          component={component}
          onClick={() => {}}
        />
      )}
    </div>
  );
}

function CarouselTypeC({ component, paused, setPaused, handleTrack }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const classNames = useClassName('CLPCarousel');

  const next = () => {
    if (selectedIndex === component.fields.slides.length - 1) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex((selectedIndex) => selectedIndex + 1);
    }
  };

  const prev = () => {
    if (selectedIndex === 0) {
      setSelectedIndex(component.fields.slides.length - 1);
    } else {
      setSelectedIndex((selectedIndex) => selectedIndex - 1);
    }
  };

  const onClick = (id) => {
    setSelectedIndex(
      component.fields.slides.findIndex((slide) => slide.sys.id === id)
    );
  };

  const selected = component.fields.slides[selectedIndex];
  return (
    <>
      <Marquee
        onClick={onClick}
        component={{
          ...component,
          fields: {
            ...component.fields,
            slides: component.fields.slides.slice(
              0,
              Math.ceil(component.fields.slides.length / 2)
            ),
          },
        }}
      />
      <Marquee
        direction="right"
        onClick={onClick}
        component={{
          ...component,
          fields: {
            ...component.fields,
            slides: component.fields.slides.slice(
              Math.ceil(component.fields.slides.length / 2)
            ),
          },
        }}
      />
      {selected && (
        <Modal
          onClose={() => setSelectedIndex(null)}
          className={classNames('marquee-modal-wrapper')}
          centered
          open={true}>
          {createPortal(
            <div
              className={classNames('close-button')}
              onClick={() => setSelectedIndex(null)}>
              <SVGIcon name="close" size="tiny" />
            </div>,
            document.getElementsByTagName('body')[0]
          )}
          <div className={classNames('modal-container')}>
            <div className={classNames('marquee-modal')}>
              {selected.sys.contentType.sys.id === 'lpAsset' ? (
                <CarouselAsset
                  slide={selected}
                  paused={paused}
                  setPaused={setPaused}
                  settings={component.fields.settings}
                />
              ) : (
                <CarouselCard
                  slide={selected}
                  paused={paused}
                  setPaused={setPaused}
                  handleTrack={handleTrack}
                />
              )}
            </div>
            <CarouselControls>
              <CarouselCounter
                current={selectedIndex + 1}
                total={component.fields.slides.length}
                next={next}
                paused={paused}
                autoplayDuration={component.fields.autoplayDuration}
                type={'Type B'}
              />
              {createPortal(
                <CarouselArrows
                  next={next}
                  prev={prev}
                  type={'Type B'}
                  style={{ position: 'fixed', zIndex: 99999, display: 'flex' }}
                  trackDirection={(direction) =>
                    handleTrack && handleTrack(direction)
                  }
                />,
                document.getElementsByTagName('body')[0]
              )}
            </CarouselControls>
          </div>
        </Modal>
      )}
    </>
  );
}

export default function Carousel({
  component,
  refs,
  handleTrack,
  isNestedChild,
}) {
  const [paused, setPaused] = useState(false);
  const [tagIndex, setTagIndex] = useState(0);
  const [setRef, styles] = useCustomStyle(component.fields.settings);
  const classNames = useClassName('CLPCarousel');
  // Opt-in: when a tag group's slide wraps its own lpCarousel (via the
  // slide's `carousel` field), render that nested carousel instead of the
  // slide's own card. Lets an outer tab level (e.g. "Main park") switch
  // between two independent inner groupTags carousels. No effect unless a
  // Contentful entry explicitly sets this flag.
  const nestedCarousel = styles['--nested-carousel'] === 'true';
  // 'main-park' styles the outer selector; 'nested-attraction' only tweaks
  // alignment on the inner (per-park) tab row rendered as its child --
  // everything else about that row keeps its normal carousel look.
  const tabsVariant = nestedCarousel
    ? 'main-park'
    : isNestedChild
    ? 'nested-attraction'
    : undefined;

  if (component.fields.type === 'Type D') {
    return <TypeDCarousel component={component} />;
  }

  if (component.fields.type === 'Type C') {
    return (
      <div ref={setRef} style={{ position: 'relative' }}>
        <CarouselTypeC
          component={component}
          paused={paused}
          setPaused={setPaused}
          handleTrack={(direction) => {
            handleTrack &&
              handleTrack({
                event: STANDARD_EVENTS.carouselSlide,
                params: {
                  carousel_name: component?.fields?.name,
                  direction,
                },
              });
          }}
        />
      </div>
    );
  }

  if (component.fields.type === 'Type E') {
    return (
      <CarouselTypeE
        component={component}
        refs={refs}
        handleTrack={(direction) => {
          handleTrack &&
            handleTrack({
              event: STANDARD_EVENTS.carouselSlide,
              params: {
                carousel_name: component?.fields?.name,
                direction,
              },
            });
        }}
      />
    );
  }

  if (component.fields.type === 'Type F') {
    return (
      <CarouselTypeF
        component={component}
        refs={refs}
        handleTrack={(direction) => {
          handleTrack &&
            handleTrack({
              event: STANDARD_EVENTS.carouselSlide,
              params: {
                carousel_name: component?.fields?.name,
                direction,
              },
            });
        }}
      />
    );
  }

  const tags = [
    ...new Set(component.fields.slides.map((a) => a.fields.tags).flat()),
  ].filter(Boolean);

  const groupTags = Boolean(component.fields.groupTags && tags.length > 0);
  // With a single tag there's nothing to switch between, so the tab row
  // (and its label) stays hidden even though content is still grouped
  // below for slide filtering purposes.
  const showTabs = groupTags && tags.length > 1;
  // Opt-in small label rendered above the tab row (e.g. "Main park",
  // "Your pass include"). No effect unless a Contentful entry sets it.
  const tabsLabel = styles['--tabs-label'];

  return (
    <div style={{ position: 'relative' }} ref={setRef}>
      {showTabs && tabsLabel && (
        <div className={classNames('tabs-label')}>{tabsLabel}</div>
      )}

      {showTabs && (
        <Tabs
          tagIndex={tagIndex}
          setTagIndex={(i) => {
            setTagIndex(i);
          }}
          tags={tags}
          handleTrack={handleTrack}
          tabsVariant={tabsVariant}
        />
      )}

      {groupTags ? (
        tags.map((tag, index) => {
          const nestedSlide =
            nestedCarousel &&
            component.fields.slides.find((slide) =>
              slide.fields?.tags?.includes(tag)
            );
          const nestedComponent = nestedSlide && nestedSlide.fields.carousel;

          return (
            <div
              key={index}
              {...(tagIndex !== index && { style: { display: 'none' } })}>
              {nestedComponent ? (
                <Carousel
                  component={nestedComponent}
                  refs={refs}
                  handleTrack={handleTrack}
                  isNestedChild
                />
              ) : (
                <Slider
                  component={component}
                  tagIndex={index}
                  paused={paused}
                  setPaused={setPaused}
                  handleTrack={handleTrack}
                />
              )}
            </div>
          );
        })
      ) : (
        <Slider
          component={component}
          paused={paused}
          setPaused={setPaused}
          handleTrack={handleTrack}
        />
      )}
    </div>
  );
}
